<script lang="ts">
  import { type PricingFeatureItem, type PricingFeaturesTableProps, PricingAudienceFilter } from './types';
  import { planGroupPricing, setPlanGroupPricing } from '$lib/helpers/pricing-data';
  import { debounce } from '$lib/helpers/browser/utils';
  import { getContext, onMount } from 'svelte';
  import {
    createFeatureKey,
    updateTierValuesWithAdditionalKeyData,
    getUniqueFeatures,
    categorizeFeatures,
    reorderCategories,
    subcategorizeFeatures,
    getTopFeaturesForAudience,
    filterFeaturesByAudiences,
  } from './pricing-features-utils';
  import PricingFeaturesTableFilter from './PricingFeaturesTableFilter.svelte';
  import { Text } from '@squareup/public-web-design-system';
  import PricingFeaturesTableRow from './PricingFeaturesTableRow.svelte';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';

  export let entryId: PricingFeaturesTableProps['entryId'];
  export let customCssClass: PricingFeaturesTableProps['customCssClass'];
  export let includeAudienceFilter: PricingFeaturesTableProps['includeAudienceFilter'];
  export let forceAudience: PricingFeaturesTableProps['forceAudience'];
  export let includedAudiences: PricingFeaturesTableProps['includedAudiences'];
  export let includeTopAudienceFeatures: PricingFeaturesTableProps['includeTopAudienceFeatures'];
  export let ctaSubnavHeight: PricingFeaturesTableProps['ctaSubnavHeight'];
  export let disableAccordion: PricingFeaturesTableProps['disableAccordion'];
  export let audienceFilterOptions: PricingFeaturesTableProps['audienceFilterOptions'];
  export let topFeaturesLabels: PricingFeaturesTableProps['topFeaturesLabels'];
  export let includedCategories: PricingFeaturesTableProps['includedCategories'];
  export let excludedCategories: PricingFeaturesTableProps['excludedCategories'];
  export let includedSubcategories: PricingFeaturesTableProps['includedSubcategories'];
  export let elevatedSubcategories: PricingFeaturesTableProps['elevatedSubcategories'];
  export let excludedSubcategories: PricingFeaturesTableProps['excludedSubcategories'];
  export let pricingFeatures: PricingFeaturesTableProps['pricingFeatures'];
  export let tooltipLabel: PricingFeaturesTableProps['tooltipLabel'];
  export let subcategoryLinks: PricingFeaturesTableProps['subcategoryLinks'];
  // is this still needed? or is it part of the table data
  export let processingFees: PricingFeaturesTableProps['processingFees'];

  const URL_FILTER_PARAM = 'filter';
  const LOCALE: string = getContext('locale') || 'en';

  // audience filter selection
  let selectedAudience: PricingAudienceFilter = PricingAudienceFilter.All;
  // set full feature list as relevant by default
  let relevantFeatures: PricingFeatureItem[] = pricingFeatures || []; // featureData;
  // list of expanded sections
  let tableHasOpenSection: boolean = false;
  // custom sticky row side offset
  let stickyRowSideOffset: number = 0;
  // sticky table header element
  let stickyHeader: HTMLDivElement;
  // track if header is sticky
  let tableHeaderIsStuck: boolean = false;
  // the sticky top features header
  let topFeaturesHeader: HTMLDivElement;
  // if the sticky top features header is stuck
  let topFeaturesHeaderIsStuck: boolean = false;
  // track if the global cta subnav is sticky
  let ctaSubnavIsStuck: boolean = false;
  // the pixel value of where the sticky table row should stick
  let stickyRowTopOffset: number = 0;
  // state to keep track of open table categories
  let openTableCategories: string[] = [];
  // list of all current top-level headers in the table
  const tableCategories: HTMLElement[] = [];
  // track which category header is sticky
  let stickyCategoryHeader: string = '';

  // check if an audience filter should be forced via contentful, dont force if the audience is not included
  if (forceAudience && (includedAudiences?.includes(forceAudience) || !includedAudiences)) {
    Object.values(PricingAudienceFilter).forEach((value) => {
      if (value.includes(forceAudience)) {
        selectedAudience = value;
      }
    });
  }

  // filter the categories and subcategories as needed based on contentful configs
  // remove any feature data that doesn't have the required data
  relevantFeatures = relevantFeatures.filter(
    (feature) => feature.category?.length > 0 && feature.category_key?.length > 0 && feature.feature?.length > 0
  );

  // only include explicitly added audiences if they were provided
  if (includedAudiences) {
    relevantFeatures = filterFeaturesByAudiences(relevantFeatures, includedAudiences);
  }

  // remove any explicitly excluded categories
  if (excludedCategories) {
    relevantFeatures = relevantFeatures.filter(
      (feature) => !excludedCategories.includes(feature.category_key?.toLowerCase())
    );
  }

  // remove any explicitly excluded subcategories
  if (excludedSubcategories) {
    relevantFeatures = relevantFeatures.filter(
      (feature) => !excludedSubcategories.includes(feature.subcategory_key?.toLowerCase())
    );
  }

  // create a list of specific categories to include
  let includedCategoryFeatureList: PricingFeatureItem[] = relevantFeatures;
  if (includedCategories) {
    includedCategoryFeatureList = relevantFeatures.filter((feature) =>
      includedCategories.includes(feature.category_key?.toLowerCase())
    );
  }

  // create a list of specific subcategories to include
  let includedSubcategoryFeatureList: PricingFeatureItem[] = includedCategoryFeatureList;
  if (includedSubcategories) {
    includedSubcategoryFeatureList = includedCategoryFeatureList.filter((feature) =>
      includedSubcategories.includes(feature.subcategory_key?.toLowerCase())
    );
  }

  // if displaying elevated subcategories, remove the others
  let elevatedSubcategoryFeatureList: PricingFeatureItem[] = [];
  if (elevatedSubcategories) {
    elevatedSubcategoryFeatureList = relevantFeatures.filter((feature) =>
      elevatedSubcategories.includes(feature.subcategory_key?.toLowerCase())
    );
  }

  // get a final list of included features (this could contain duplicates, which will be filtered later)
  let allIncludedFeatures: PricingFeatureItem[];
  if (elevatedSubcategoryFeatureList.length > 0) {
    // elevated subcategories override all other features
    allIncludedFeatures = elevatedSubcategoryFeatureList;
  } else {
    allIncludedFeatures = [...includedCategoryFeatureList, ...includedSubcategoryFeatureList];
  }

  // add keys to each feature to use for ids
  const keyedFeatureData = allIncludedFeatures.map((feature) => ({
    ...feature,
    key: createFeatureKey(`${feature.category}_${feature.subcategory}_${feature.feature}`),
  }));

  // monitor sub2 api call load
  $: isApiLoading = $planGroupPricing.ok === null;

  // aggregate the static processing fee data from pwr and the fetched price data from sub2
  $: feeAndPriceData = {
    ...processingFees,
    ...$planGroupPricing,
  };

  // add the final feature data with additional key data
  $: completeTableData = keyedFeatureData.map((kd) => updateTierValuesWithAdditionalKeyData(feeAndPriceData, kd));

  // generate an array of features matching the selected audience filter, excluding duplicates across audiences
  $: filteredTableData = getUniqueFeatures(
    completeTableData.filter((feature) => {
      if (selectedAudience === PricingAudienceFilter.All) {
        return true;
      }
      return feature[selectedAudience];
    })
  );

  // build the list of top features for the selected audience
  $: topFeatures = getTopFeaturesForAudience(filteredTableData, selectedAudience);

  $: showingTopFeatures =
    topFeatures.length > 0 && includeTopAudienceFeatures && selectedAudience !== PricingAudienceFilter.All;

  // reorder the filtered feature data so it can be easily rendered in the table
  $: templateCategories = elevatedSubcategories
    ? reorderCategories(elevatedSubcategories, subcategorizeFeatures(filteredTableData, elevatedSubcategories))
    : reorderCategories(includedCategories, categorizeFeatures(filteredTableData));

  // check if an audience filter should be forced via url param
  function checkForUrlFilter() {
    // look for the parameter
    const urlParams = new URLSearchParams(window.location.search);
    const filterValue = urlParams.get(URL_FILTER_PARAM);
    // check if it is valid
    if (filterValue) {
      let validParam = false;
      Object.values(PricingAudienceFilter).forEach((value) => {
        if (value.split('_')[1] === filterValue?.toLowerCase()) {
          // so valid, set the filter
          selectedAudience = value;
          validParam = true;
        }
      });
      if (!validParam) {
        // remove invalid parameter values
        const url = new URL(window.location.href);
        url.searchParams.delete(URL_FILTER_PARAM);
        window.history.replaceState({}, document.title, url.toString());
      }
    }
  }

  // track accordion toggling so the table knows when the header needs to be sticky
  async function toggleDetails(e: ToggleEvent, categoryKey: string) {
    if (e.newState === 'open') {
      openTableCategories = [...openTableCategories, categoryKey];
    } else {
      openTableCategories = openTableCategories.filter((key) => key !== categoryKey);
    }

    tableHasOpenSection = openTableCategories.length > 0;
  }

  // handle table filtering from the filter buttons
  function onFilter(e: CustomEvent) {
    // update the filter
    selectedAudience = e.detail;
    // update the url with the filter parameter (for copy+paste sharing)
    const url = new URL(window.location.href);
    // convert filter values to simple stings for the url, eg: "filter_all" -> "all"
    const simpleFilterValue = e.detail.split('_')[1];
    if (simpleFilterValue) {
      url.searchParams.set(URL_FILTER_PARAM, simpleFilterValue);
      window.history.replaceState({}, document.title, url);
    }
  }

  // check if the sticky nav is in its sticky state or not
  function onSubnavObserve(mutations: MutationRecord[]) {
    let anySubnavStuck = false;
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
        const element = mutation.target as HTMLElement;
        if (element.classList.contains('isSticky')) {
          anySubnavStuck = true;
        }
      }
    });
    ctaSubnavIsStuck = anySubnavStuck;
  }

  // set the sticky header row top offset position
  function setStickyRowTopOffset() {
    // subnav height if stuck, 0 if not stuck
    stickyRowTopOffset = ctaSubnavIsStuck ? ctaSubnavHeight : 0;
  }

  const onResize = debounce(() => {
    // set sticky row left position
    stickyRowSideOffset = stickyHeader?.getBoundingClientRect().left;
    setStickyRowTopOffset();
  }, 100);

  // track scrolling to know if the table header is sticky or not
  const onScroll = () => {
    // set the sticky row top offset to match the cta subnav height
    setStickyRowTopOffset();
    // check if the table header row is sticky
    tableHeaderIsStuck = stickyHeader?.getBoundingClientRect().top <= stickyRowTopOffset;
    // check if top features header is sticky
    topFeaturesHeaderIsStuck = topFeaturesHeader?.getBoundingClientRect().top <= stickyRowTopOffset;
    // check if tabel categories are sticky
    stickyCategoryHeader = '';
    tableCategories.forEach((category) => {
      const categoryHeader = category?.querySelector('.category-summary');
      if (categoryHeader && categoryHeader.getBoundingClientRect().top <= stickyRowTopOffset) {
        // save the current sticky header
        stickyCategoryHeader = category.id;
      }
    });
  };

  const onHashChange = () => {
    const hashValue = window.location.hash.slice(1);
    if (templateCategories.some((categories) => categories.key === hashValue)) {
      // if the hash matches a category, open it as long it's not already open
      if (!openTableCategories.includes(hashValue)) {
        openTableCategories = [...openTableCategories, hashValue];
      }
      (document.getElementById(hashValue) as HTMLDivElement)?.scrollIntoView();
      tableHasOpenSection = openTableCategories.length > 0;
    }
  };

  onMount(() => {
    setPlanGroupPricing(LOCALE);
    onResize();
    onHashChange();

    // only allow the url to set the audience filter if the audience filter is also included
    if (includeAudienceFilter) {
      checkForUrlFilter();
    }

    // observer the global subnav
    const subnavObserver = new MutationObserver(onSubnavObserve);

    // desktop subnav (try both new and old versions of subnav, just in case)
    const newDesktopNav = document.querySelector('nav .cta-subnav-container');
    const oldDesktopNav = document.querySelector('nav.CtaSubnavDesktopNav .header');
    const ctaDesktopSubnav = newDesktopNav ?? oldDesktopNav;
    if (ctaDesktopSubnav) {
      subnavObserver.observe(ctaDesktopSubnav, { attributes: true, attributeFilter: ['class'] });
    }
    // mobile subnav (try both new and old versions of subnav, just in case
    const newMobileNav = document.querySelector('nav .cta-subnav-menu-container');
    const oldMobileNav = document.querySelector('nav.CtaSubnavMobileNav .header');
    const ctaMobileSubnav = newMobileNav ?? oldMobileNav;
    if (ctaMobileSubnav) {
      subnavObserver.observe(ctaMobileSubnav, { attributes: true, attributeFilter: ['class'] });
    }

    // on unmount
    return () => {
      subnavObserver.disconnect();
    };
  });
</script>

<svelte:window on:resize={onResize} on:scroll={onScroll} on:hashchange={onHashChange} />
<div
  class="PricingFeaturesTable PricingFeaturesTable-{entryId}-box-properties {customCssClass}"
  style="--sticky-nav-top-offset: {stickyRowTopOffset}px; --sticky-nav-side-offset: {stickyRowSideOffset}px;"
  data-entry-id={entryId}
>
  {#if includeAudienceFilter}
    <div class="filter-holder">
      <PricingFeaturesTableFilter
        filters={audienceFilterOptions}
        selectedFilter={selectedAudience}
        on:filter={onFilter}
      />
    </div>
  {/if}
  <div class="pricing-table" role="table">
    <!-- column Headers -->
    <div
      class="sticky-table-header"
      class:stuck={tableHeaderIsStuck}
      class:stickable={tableHasOpenSection || showingTopFeatures || disableAccordion}
      class:reveal={!isApiLoading}
      role="rowgroup"
      bind:this={stickyHeader}
    >
      <div role="row" class="header-row">
        <!-- intentionally left blank -->
        <div role="columnheader" class="feature-name-header">&nbsp;</div>
        {#if $planGroupPricing?.plan_group && !isApiLoading}
          {#each $planGroupPricing?.plan_group?.plans as planTier}
            <div role="columnheader" class="feature-tier-header">
              <Text variant="100">{planTier.name}</Text>
            </div>
          {/each}
        {:else if isApiLoading}
          <div role="columnheader" class="feature-tier-header">
            <!-- blank to preseve height while loading -->
            <Text variant="100">&nbsp;</Text>
          </div>
        {/if}
      </div>
    </div>
    <!-- only show top features when audience filter is present, and filtered by an audience -->
    {#if showingTopFeatures && selectedAudience !== PricingAudienceFilter.All}
      <div class="top-features" role="rowgroup" aria-labelledby={`${selectedAudience}-top-features`}>
        {#if topFeaturesLabels && topFeaturesLabels[selectedAudience]}
          <div
            id={`${selectedAudience}-top-features`}
            class="top-features-header"
            class:stuck={topFeaturesHeaderIsStuck}
            bind:this={topFeaturesHeader}
          >
            <Text variant="200" as="h3">{topFeaturesLabels[selectedAudience]}</Text>
          </div>
        {/if}
        <!-- top features for the selected audience -->
        <div class="top-features-holder">
          {#each topFeatures as feature, index (feature.key)}
            <PricingFeaturesTableRow {feature} first={index === 0} {tooltipLabel} />
          {/each}
        </div>
      </div>
    {/if}
    {#each templateCategories as category, categoryIndex (category.key)}
      {@const isTemplateOpen = disableAccordion || openTableCategories.includes(category.key)}
      <details
        id={`${category.key}-details`}
        class="category-details"
        class:accordion-disabled={disableAccordion}
        class:without-subcategories={categoryIndex > 0 && !category.subcategories}
        open={isTemplateOpen}
        on:toggle={(e) => toggleDetails(e as unknown as ToggleEvent, category.key)}
        bind:this={tableCategories[categoryIndex]}
      >
        <!-- category/sabcategory header -->
        <summary
          id={`${category.key}${category.features ? '-flat' : ''}`}
          tabindex={disableAccordion ? -1 : 0}
          class="category-summary"
          class:stuck={stickyCategoryHeader === `${category.key}-details`}
          class:disabled={disableAccordion}
        >
          <Text variant="200" as="h3">{category.name}</Text>
          <span class="category-toggle"></span>
        </summary>
        <!-- category/subcategory content -->
        <div role="rowgroup" id={category.key} class="category" aria-labelledby={category.key}>
          <!-- if category includes subcategories -->
          <div class="category-content-wrapper">
            <div class="category-content">
              {#if category.subcategories}
                {#each category.subcategories as subcategory (subcategory.key)}
                  <div class="subcategory" role="rowgroup" aria-labelledby={subcategory.key}>
                    <!-- only render subheading if it has text in it -->
                    {#if String(subcategory.name).replaceAll(/\s+/g, '').length > 0}
                      <div id={subcategory.key} class="subcategory-header">
                        <Text variant="200" as="h4">{subcategory.name}</Text>
                      </div>
                    {/if}
                    <!-- category -> subcategory -> features -->
                    {#if subcategory.features}
                      {#each subcategory.features as feature, index (feature.key)}
                        <PricingFeaturesTableRow {feature} first={index === 0} {tooltipLabel} />
                      {/each}
                    {/if}
                  </div>
                  <!-- optional subcategory link -->
                  {@const subcatLink = subcategoryLinks.find((link) => link.subcatKey === subcategory.key)}
                  {#if subcatLink}
                    <div class="subcategory-link">
                      <DynamicChildrenComponent children={[subcatLink.link]} />
                    </div>
                  {/if}
                {/each}
                <!-- if category has no subcategories -->
              {:else if category.features}
                <!-- subcategory -> features -->
                {#each category.features as feature, index (feature.key)}
                  <PricingFeaturesTableRow {feature} first={index === 0} {tooltipLabel} />
                {/each}
              {/if}
            </div>
          </div>
        </div>
      </details>
    {/each}
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;

  @layer custom-components {
    .PricingFeaturesTable {
      --hover-padding: var(--space-10);
      --feature-name-column-width: 40%;
      --feature-tier-column-width: 20%;
      --custom-column-header-size-base: 1.2rem;
      --custom-feature-header-size-base: 1.6rem;

      .pricing-table {
        position: relative;
      }

      .sticky-table-header {
        transition:
          top var(--duration-default),
          opacity var(--duration-default);
        background-color: var(--color-white);
        padding: var(--space-10) 0;
        opacity: 0;

        &.reveal {
          opacity: 1;
        }

        &.stickable {
          position: sticky;
          top: var(--sticky-nav-top-offset);
          z-index: 1;

          &.stuck {
            &::before {
              content: '';
              position: absolute;
              top: 0;
              left: calc(-1 * var(--sticky-nav-side-offset));
              width: calc(100% + (var(--sticky-nav-side-offset) * 2));
              height: 100%;
              background-color: var(--color-white);
              z-index: 1;
              border-bottom: 1px solid var(--color-neutral-800);
            }
          }
        }

        @include mediaQueries.at('large') {
          top: var(--sticky-nav-top-offset);
        }
      }

      .filter-holder {
        margin-bottom: var(--space-40);

        @include mediaQueries.at('medium') {
          margin-bottom: var(--space-80);
        }
      }

      .top-features {
        position: relative;
        width: calc(100% + (var(--hover-padding) * 2));
        margin-bottom: var(--space-20);

        .top-features-header {
          padding-top: var(--space-20);
          padding-bottom: var(--space-30);

          :global(h3) {
            transition: font-size var(--duration-quick);
          }

          &.stuck {
            :global(h3) {
              font-size: 1.8rem;
              width: var(--feature-name-column-width);
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }

          @include mediaQueries.at('medium') {
            position: sticky;
            top: var(--sticky-nav-top-offset);
            padding-top: var(--space-10);
            padding-bottom: var(--space-40);
            margin: 0 var(--hover-padding);
            z-index: 1; // keep the header above the table category-summary
          }
        }

        @include mediaQueries.at('medium') {
          // allow the row hover background to extend past its edges
          margin-left: calc(var(--hover-padding) * -1);
        }
      }

      .category-details {
        &.accordion-disabled {
          height: auto !important; // prevent accordion transitions
        }

        &.without-subcategories:last-child:not(:first-child) {
          .category-summary {
            border-bottom: 0;
          }
        }

        @include mediaQueries.until('medium') {
          :global {
            h4 {
              --font-size: var(--custom-feature-header-size-base);
            }
          }
        }
      }

      .category-summary {
        position: relative;
        display: flex;
        width: 100%;
        height: var(--space-80);
        align-items: center;
        border-bottom: 1px solid var(--color-neutral-800);
        list-style: none;
        user-select: none;
        cursor: pointer;

        &::-webkit-details-marker {
          display: none;
        }

        &::marker {
          content: '';
          display: none;
        }

        :global(h3) {
          transition:
            margin var(--duration-quick) linear,
            padding var(--duration-quick) linear,
            font-size var(--duration-quick) linear;
        }

        &.disabled {
          pointer-events: none;

          .category-toggle {
            display: none;
          }
        }

        @include mediaQueries.at('medium') {
          height: var(--space-100);
        }

        @include mediaQueries.at('large') {
          height: var(--space-120);
        }
      }

      .category {
        position: relative;

        .category-content-wrapper {
          overflow: hidden;

          // in unexpanded sections, keep the first row top border hidden so it doesnt double up with the header bottom border when collapsing
          :global(.first .feature-row-content) {
            border-top: 1px solid var(--color-white);
          }

          @include mediaQueries.at('medium') {
            // allow the row hover background to extend past its edges
            margin-left: calc(var(--hover-padding) * -1);
            width: calc(100% + (var(--hover-padding) * 2));
          }
        }
      }

      // the open state
      .category-details[open] {
        .category-toggle {
          &::before {
            transform: translate3d(-50%, -50%, 0) rotate(-180deg);
          }

          &::after {
            transform: translate3d(-50%, -50%, 0) rotate(-90deg) scaleX(0);
          }
        }

        .category-summary {
          --sticky-label-adjust-small: -5.2rem;
          --sticky-label-adjust-large: -7.3rem;

          border-bottom: 1px solid var(--color-white);

          @include mediaQueries.at('medium') {
            position: sticky;
            top: var(--sticky-nav-top-offset);

            &.stuck {
              z-index: 2;
              border-bottom: 0;
              // hide user selection and interactivity for stuck headers
              pointer-events: none;
              user-select: none;
              outline: none;

              .category-toggle {
                display: none;
              }

              :global(h3) {
                font-size: 1.8rem;
                margin-top: var(--sticky-label-adjust-small);
                z-index: 2; // keep the category title above the category-summary
                width: var(--feature-name-column-width);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
            }
          }

          @include mediaQueries.at('large') {
            &.stuck :global(h3) {
              margin-top: var(--sticky-label-adjust-large);
            }
          }
        }

        .category-content {
          // for tables without subcategories, add space after the final feature
          :global(> .feature-row:last-child) {
            margin-bottom: var(space-20);
          }
        }
      }

      .subcategory {
        margin-bottom: var(--space-20);

        .subcategory-header {
          display: flex;
          align-items: center;
          height: var(--space-60);
          border-bottom: 1px solid var(--color-neutral-800);

          :global(h4) {
            --color: var(--color-gray-55);
          }

          @include mediaQueries.at('medium') {
            height: calc(var(--space-60) + var(--space-10));
            margin: 0 var(--hover-padding);
          }
        }
      }

      .subcategory-link {
        margin-bottom: var(--space-40);

        @include mediaQueries.at('medium') {
          margin-left: var(--space-10);
        }
      }

      .header-row {
        position: relative;
        display: flex;
        z-index: 1;

        .feature-name-header {
          display: none;

          @include mediaQueries.at('medium') {
            display: block;
            flex: 1 0 var(--feature-name-column-width);
          }
        }

        .feature-tier-header {
          flex: 1 0 var(--feature-tier-column-width);
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;

          @include mediaQueries.until('medium') {
            :global(p) {
              --font-size: var(--custom-column-header-size-base);
            }
          }
        }
      }

      .category-toggle {
        --accordion-toggle-background-color: var(--color-gray-99);
        --accordion-toggle-line-height: 0.4rem;
        --accordion-toggle-size: 2rem;

        position: absolute;
        right: 0;
        top: 50%;
        margin-top: calc(var(--accordion-toggle-size) * -0.5); // center the +/-
        height: var(--accordion-toggle-size);
        width: var(--accordion-toggle-size);

        @include mediaQueries.at('medium') {
          --accordion-toggle-line-height: 0.4rem;
          --accordion-toggle-size: 2.8rem;
        }

        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          height: var(--accordion-toggle-line-height);
          width: var(--accordion-toggle-size);
          background-color: var(--accordion-toggle-background-color);
          transition: transform var(--duration-slow) var(--easing-sharp);
          transform: translate3d(-50%, -50%, 0);
        }

        &::after {
          content: '';
          position: absolute;
          top: 50%;
          left: 50%;
          height: var(--accordion-toggle-line-height);
          width: var(--accordion-toggle-size);
          background-color: var(--accordion-toggle-background-color);
          transition: transform var(--duration-slow) var(--easing-sharp);
          transform: translate3d(-50%, -50%, 0) rotate(90deg);
        }
      }
    }
  }
</style>
